/**
 * The search box and the list it finds. It searches as you type, a moment after the last
 * key, so at the booth nobody has to press a button.
 */

import { useEffect, useRef, useState } from "react";
import { ChevronRight, Search as SearchIcon, SearchX, X } from "lucide-react";
import { api, type CardHit, type Language } from "./api";
import { cardNumber, LANGUAGE_LABEL, usd } from "./format";
import { CardImage, Chip, cx, EmptyState, Segmented, Skeleton } from "./ui";

const LANGUAGES = [
  { value: "", label: "All" },
  { value: "en", label: "EN" },
  { value: "ja", label: "JP" },
  { value: "zh", label: "CN" },
  { value: "ko", label: "KR" },
] as const;

const EXAMPLES = ["mew 183 jp", "charizard 4/102", "umbreon vmax 215", "s12a", "pikachu master ball 151"];

export function Search({ onOpen, onError }: { onOpen: (id: number) => void; onError: (e: unknown) => void }) {
  const [text, setText] = useState("");
  const [language, setLanguage] = useState<Language | "">("");
  const [cards, setCards] = useState<CardHit[]>([]);
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");
  const latest = useRef(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const query = text.trim();
    if (query.length < 2) {
      setCards([]);
      setState("idle");
      return;
    }
    const ticket = ++latest.current;
    setState("busy");
    const timer = setTimeout(() => {
      api
        .search(query, language || undefined)
        .then((r) => {
          if (ticket !== latest.current) return;
          setCards(r.cards);
          setState("done");
        })
        .catch((e) => {
          if (ticket !== latest.current) return;
          onError(e);
          setState("error");
        });
    }, 220);
    return () => clearTimeout(timer);
  }, [text, language, onError]);

  return (
    <div>
      <div className="sticky top-14 z-20 border-b border-slate-200 bg-slate-100/95 px-4 pb-3 pt-4 backdrop-blur">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            ref={input}
            type="search"
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="field h-[52px] rounded-2xl pl-12 pr-11 text-base shadow-card"
            placeholder="Name, set or number"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {text && (
            <button
              type="button"
              aria-label="Clear"
              onClick={() => {
                setText("");
                input.current?.focus();
              }}
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-slate-200 text-slate-600 hover:bg-slate-300"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <Segmented size="sm" value={language} options={LANGUAGES} onChange={setLanguage} />
          {state === "done" && <span className="num text-xs text-slate-500">{cards.length === 30 ? "30+" : cards.length} found</span>}
        </div>
      </div>

      <div className="px-4 pt-4">
        {state === "idle" && (
          <div className="card p-5">
            <div className="font-semibold text-slate-800">Find a card</div>
            <p className="mt-1 text-sm text-slate-500">
              Type the name, the set or its code, or the number on the card. Words can be mixed and in any order, and Japanese names work too.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => setText(ex)}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[13px] text-slate-700 transition hover:border-navy-200 hover:bg-navy-50"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {state === "busy" && (
          <div className="card divide-y divide-slate-100 overflow-hidden">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-[62px] w-11 flex-none rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/5" />
                  <Skeleton className="h-3 w-3/5" />
                </div>
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        )}

        {state === "error" && (
          <EmptyState icon={<SearchX className="h-6 w-6" />} title="The search did not go through">
            Check the connection and try again.
          </EmptyState>
        )}

        {state === "done" && cards.length === 0 && (
          <EmptyState icon={<SearchX className="h-6 w-6" />} title="No cards found">
            Try fewer words, or the number as printed on the card, such as 183/172.
          </EmptyState>
        )}

        {state === "done" && cards.length > 0 && (
          <ul className="card divide-y divide-slate-100 overflow-hidden animate-fadein">
            {cards.map((card) => (
              <li key={card.id}>
                <button
                  type="button"
                  onClick={() => onOpen(card.id)}
                  className="group flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-slate-50 active:bg-slate-100"
                >
                  <CardImage src={card.imageUrl} alt="" className="w-11 flex-none shadow-sm" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-slate-900">{card.cardName}</div>
                    <div className="mt-1 flex items-center gap-1.5">
                      <Chip tone="navy">{LANGUAGE_LABEL[card.language]}</Chip>
                      <span className="num text-[13px] font-medium text-slate-700">{cardNumber(card.number, card.setTotal)}</span>
                      {card.variants.map((v) => (
                        <Chip key={v} tone="gold">{v}</Chip>
                      ))}
                    </div>
                    <div className="mt-1 truncate text-xs text-slate-500">
                      {card.setName}
                      {card.localName && <span className="text-slate-400"> · {card.localName}</span>}
                    </div>
                  </div>
                  <div className="flex-none text-right">
                    <div className="num text-[15px] font-semibold text-slate-900">{usd(card.ungraded)}</div>
                    <div className="mt-0.5 whitespace-nowrap text-[11px]">
                      <span className="font-medium text-slate-400">PSA 10 </span>
                      <span className={cx("num font-semibold", card.psa10 ? "text-gold-700" : "text-slate-300")}>{usd(card.psa10)}</span>
                    </div>
                  </div>
                  <ChevronRight className="hidden h-4 w-4 flex-none text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-slate-400 sm:block" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
